/**
 * The one status line every integration surface renders — the tone
 * vocabulary, the class each tone paints in, and the shape itself.
 *
 * It lives at the integrations domain root rather than inside one provider
 * because three provider forms here (gemini, revolut, meta) and every row on
 * the admin panel next door render the same four-tone line. Each had its own
 * verbatim copy of the table and of the shape until this file.
 *
 * `at` names the one capability the line actually uses — yielding a Date —
 * rather than the Firestore Timestamp its production writer happens to hold.
 * A real Timestamp satisfies it with no cast, and so does a fixture: these
 * views mount against the store in the storefront and against fixtures in the
 * studio, so a type only the store can produce would make the studio unable
 * to mount them at all.
 */
import type { TextKey } from "@momo/i18n";

/** How a status line reads at a glance. */
export type IntegrationTone = "neutral" | "success" | "warning" | "error";

export const INTEGRATION_TONE_CLASS: Record<IntegrationTone, string> = {
	neutral: "text-surface-onVariant",
	success: "text-success-default",
	warning: "text-warning-default",
	error: "text-error-default",
};

/** The status line an integration surface renders: the fact, its tone, and
 *  the date the fact holds as of. The fact rides as its catalog key and
 *  params, never as finished text: the line is derived in a `computed`, which
 *  a language switch does not re-run, while every surface rendering it does
 *  re-render — so the surface translates, in the language on screen. */
export type IntegrationStatusLine = {
	readonly tone: IntegrationTone;
	readonly message: TextKey;
	readonly params: Readonly<Record<string, string>>;
	readonly at: { toDate(): Date } | null;
};

/** The line every surface shows before a credential has ever been saved. */
export const NOT_CONFIGURED: IntegrationStatusLine = {
	tone: "neutral",
	message: "admin.integrations.status.notConfigured",
	params: {},
	at: null,
};
